/**
 * Utility functions for package lookup and normalization
 */

export const normalizePackageKey = (str) => {
  if (!str) return ''
  try {
    str = decodeURIComponent(str)
  } catch (e) {
    // ignore
  }
  return str
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Finds a matching package from the packages list using multiple resilient strategies:
 * 1. MongoDB _id
 * 2. Package Code (e.g. DHMY073)
 * 3. Exact Name (case-insensitive, trimmed)
 * 4. Normalized Name (hyphens, underscores, multiple spaces, tabs normalized)
 * 5. Exact City
 * 6. Normalized City
 * 7. Substring / Slug match fallback
 */
export const findMatchingPackage = (packages, searchKey) => {
  if (!Array.isArray(packages) || !packages.length || !searchKey) return null

  const rawKey = String(searchKey).trim()
  const lowerKey = rawKey.toLowerCase()
  const normalizedKey = normalizePackageKey(rawKey)

  // 1. Direct ID match
  const byId = packages.find((p) => p._id && String(p._id) === rawKey)
  if (byId) return byId

  // 2. Direct code match
  const byCode = packages.find((p) => (p.code || '').trim().toLowerCase() === lowerKey)
  if (byCode) return byCode

  // 3. Exact name match (case-insensitive, trimmed)
  const byExactName = packages.find((p) => (p.name || '').trim().toLowerCase() === lowerKey)
  if (byExactName) return byExactName

  // 4. Normalized name match (dashes, spaces, tabs collapsed)
  const byNormName = packages.find((p) => normalizePackageKey(p.name) === normalizedKey)
  if (byNormName) return byNormName

  // 5. City match (exact trimmed)
  const byExactCity = packages.find((p) => (p.city || '').trim().toLowerCase() === lowerKey)
  if (byExactCity) return byExactCity

  // 6. City match (normalized)
  const byNormCity = packages.find((p) => normalizePackageKey(p.city) === normalizedKey)
  if (byNormCity) return byNormCity

  // 7. Partial/contains fallback (if search key is distinct enough)
  if (normalizedKey.length > 3) {
    const byContains = packages.find((p) => {
      const pNorm = normalizePackageKey(p.name)
      return pNorm && (pNorm.includes(normalizedKey) || normalizedKey.includes(pNorm))
    })
    if (byContains) return byContains
  }

  return null
}
